/* eslint-disable no-param-reassign */
import { transaction } from '@src/slide/domain/slide-app';
import { observable, toJS } from 'mobx';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import i18n from '@i18next/i18n';
import { MenuType } from '@src/slide/domain/ui-model/constant/menu'; import { SelectedEleMgrType } from '@src/slide/domain/selection/constant/select-element-type';
import { PPTEditorStateType } from '@src/slide/domain/ui-model/constant/edit-state-type';
import { ActionType } from '@src/framework/editor/src/js/constants/common-action-type';
import { IAnimBrushManager, IAnimBrushServiceDelegates, TAnimBrushState } from './interface';
import { AnimType, DEFAULT_CLICK_SPID } from '@tencent/slide-sdk-model';
import EditorAnimPreviewManager from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/editor-state-animation/editor-anim-preview-manager';
import { presetClassTypes } from '@src/slide/domain/ui-model/element-modeles/property/animation/constant';
import { IAnimModel } from '@src/slide/domain/ui-model/element-modeles/Interface/anim-model.interface';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { IGroupItem } from '@src/framework/frame/components/tool-bar/common/group-disable';
import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import { EMPH_LIST } from '@src/slide/domain/ui-model/element-modeles/property/animation/constants';

export type TProcessMenuItem = IGroupItem & {
    canDoubleClick?: boolean,
    enable?: boolean
};

export default class AnimBrushManager implements IAnimBrushManager {
    public readonly id = ActionType.ANIM_BRUSH;
    @observable
    public animBrushState?: TAnimBrushState;
    private isContinuous = false;
    private sourcePageId;
    private services: IAnimBrushServiceDelegates;

    public constructor(services: IAnimBrushServiceDelegates) {
        this.services = services;
    }

    public updateServiceDelegates(services): void {
        this.services = {
            ...this.services,
            ...services,
        };
    }

    public onDoubleClickButton() {
        this.isContinuous = true;
        this.beginBrush();
        tdw({
            opername: 'doc_ppt',
            module: 'anim_brush',
            action: 'double',
        });
    }

    public onClickButton() {
        if (this.animBrushState) {
            this.onCancelBrush();
        } else {
            this.beginBrush();
            tdw({
                opername: 'doc_ppt',
                module: 'anim_brush',
                action: 'thin',
            });
        }
    }

    public clearAnimBrushState = (): void => {
        if (this.animBrushState) { // 有数据才清空，防止反复刷新
            this.animBrushState = undefined;
            this.sourcePageId = '';
        }
    };

    public onCancelBrush() {
        this.isContinuous = false;
        this.clearAnimBrushState();
    }

    /**
     * NOTE:
     * src/slide/application/service/menu-data-worker/core/interface.ts 中 item 类型为 any
     * 此处类型定义参考 @see src/slide/application/service/menu-data-worker/core/index.ts
     */
    public processMenuItemData(item: TProcessMenuItem, type: string) {
        if (type === MenuType.TOOLBAR) {
            let tip = i18n.t('复制所选对象的动画，应用到其他对象上。双击可将对象复制到多个对象上。');
            let canDoubleClick = true;
            if (this.animBrushState) {
                tip = i18n.t('退出动画刷(Esc)');
                canDoubleClick = false;
            }
            item.tipMsg = tip;
            item.canDoubleClick = canDoubleClick;
        }
        // 可写
        const value = !!this.animBrushState;
        // 可读
        const support = !!this.animList.length;
        // 有权限
        item.enable = value || (support
            && this.services?.userPriviledgeService.pptEditorState < PPTEditorStateType.COPYABLE);
    }

    public processMenuItemValues(itemValues: Record<string, any>) {
        const value = this.animBrushState;
        itemValues[ActionType.ANIM_BRUSH] = value;
        return itemValues;
    }

    public onElementSelectionMouseUp() {
        if (this.services.userPriviledgeService.pptEditorState > PPTEditorStateType.EDITABLE) {
            this.services.tipsManager.showCannotEditTips();
        } else {
            this.applyAnimEffects();
        }
        // 更新：ppt 中动画刷点击原元素不清除动画刷，可继续使用
        if (!this.isContinuous && !this.isSelectElOriginEl()) {
            this.onCancelBrush();
        }
    }

    private beginBrush() {
        if (this.hasPathAnim()) {
            this.services.tipsManager.showMsg(i18n.t('动画刷暂不支持路径动画'));
        }
        const props = this.getElementState();
        if (props) {
            this.animBrushState = props;
        }
    }

    /**
     * 读取来源对象的属性
     */
    private getElementState(): TAnimBrushState | undefined {
        const { selectedElements, currentSlide } = this.services.selectionService;
        // 只对单个元素进行动画复制
        if (selectedElements.length === 1) {
            const elModel = selectedElements.selectedArray?.[0] as IElementBaseModel;
            // 过滤路径动画
            const effects = this.animList.filter(anim => anim?.props?.presetClass !== presetClassTypes.PATH);
            const allMediaEffect = this.isAllMediaEffect(effects);

            this.sourcePageId = currentSlide?.id;
            return {
                originElId: elModel?.uniqueID || '',
                effects,
                allMediaEffect,
            };
        }

        return;
    }

    // 检查是否是路径动画
    private hasPathAnim(): boolean {
        const pathAnimCount = this.animList.filter(anim =>
            anim?.props?.presetClass === presetClassTypes.PATH).length;
        return !!pathAnimCount;
    }

    /**
     * 检查是否全为媒体类动画
     * @param effect 过滤后的效果
     * @returns boolean
     */
    private isAllMediaEffect(effects: IAnimModel[]): boolean {
        const notMediaEffects = effects.filter(anim =>
            ![AnimType.Play, AnimType.Pause, AnimType.Stop].includes(anim?.props?.animType as AnimType));

        return notMediaEffects.length <= 0;
    }

    /**
     * 在目标对象上应用属性
     */
    private applyAnimEffects() {
        const { selectedElements, currentSlide } = this.services.selectionService;
        if (!selectedElements.length && !selectedElements.selectedArrayForType) {
            return;
        }
        const selectedSlideElements = selectedElements.selectedArrayForType(SelectedEleMgrType.SLIDE);
        if (!selectedSlideElements.length) return;

        const { animBrushState } = this;
        const brushState = toJS(animBrushState);
        if (!brushState) return;
        const targetEl = selectedSlideElements?.[0] as IPictureModel;
        // 不对动画源元素重绑，重绑会多出一条历史记录
        if (targetEl.uniqueID === brushState.originElId) return;

        let applyEffects = this.filtSupportAnim(targetEl);

        // 跨页复制动画，将非主时间线且非自身为触发器的动画的触发器修改为主时间线上
        if (currentSlide?.id !== this.sourcePageId) {
            applyEffects = applyEffects.map((anim) => {
                if (![DEFAULT_CLICK_SPID, animBrushState?.originElId].includes(anim.clickSpid)) {
                    return {
                        ...anim,
                        clickSpid: DEFAULT_CLICK_SPID,
                    };
                }

                return anim;
            });
        }

        // 如果过滤后的动画为空则不进行绑定，避免删除原元素的动画
        if (!applyEffects.length) return;

        transaction(() => {
            currentSlide?.bindAnimation({
                uiModel: targetEl,
                animEffects: applyEffects,
                originId: brushState.originElId,
                rebind: true,
            });
        });

        // 绑完播放
        this.playSelectAnimation();
    }

    private playSelectAnimation() {
        const selectEl = this.services.selectionService.selectedElements;
        if (selectEl?.length === 1) {
            const elModel = selectEl.selectedArray?.[0];
            const { animList = [] } = elModel || {};
            const previewManager = EditorAnimPreviewManager.getInstance();
            if (previewManager && animList.length) {
                previewManager.previewOneToOneAnims(animList, true);
            }
        }
    }

    // 判断当前选中元素是否是动画刷动画来源元素
    private isSelectElOriginEl = (): boolean => {
        const { selectedElements } = this.services.selectionService;
        if (!selectedElements.length && !selectedElements.selectedArrayForType) {
            return false;
        }
        const selectedSlideElements = selectedElements.selectedArrayForType(SelectedEleMgrType.SLIDE);
        if (!selectedSlideElements.length) return false;

        const { animBrushState } = this;
        const brushState = toJS(animBrushState);
        if (!brushState) return false;
        const targetEl = selectedElements.selectedArray?.[0];
        return targetEl?.uniqueID === brushState.originElId;
    };

    private get animList(): IAnimModel[] {
        const { selectedElements } = this.services.selectionService;
        if (selectedElements.length === 1) {
            const elModel = selectedElements.selectedArray?.[0] as IElementBaseModel;
            // 剔除非动画 或 undefined
            const animList = elModel?.animList.filter(anim => !!anim) || [];
            return animList;
        }
        return [];
    }

    // 过滤出目标元素支持的动画
    private filtSupportAnim = (targetEl: IPictureModel) => {
        let animEffects = this.animBrushState?.effects || [];
        if (!animEffects.length) return [];

        // 过滤其中不支持的强调动画
        if (this.services.animCategoryManager) {
            const list = this.services.animCategoryManager.getAnimCategoryList(EMPH_LIST);
            const animList = list.reduce((pre, cur) => [...pre, ...cur.children], []);
            animEffects  = animEffects.filter((anim: IAnimModel) => {
                const hasAnim = animList.find(a => a.id === anim.props.animType);
                return hasAnim ? !hasAnim.disabled : true;
            });
        }

        // 对非音视频类元素绑定动画时过滤掉音视频的开始、结束、暂停动画
        if (!targetEl?.audio && !targetEl?.video) {
            animEffects = animEffects
                .filter(anim =>
                    ![AnimType.Play, AnimType.Pause, AnimType.Stop].includes(anim?.props?.animType as AnimType));
        }

        // 过滤路径动画
        animEffects = animEffects.filter(anim => anim?.props?.presetClass !== presetClassTypes.PATH);

        return animEffects;
    };
}
